import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  AppLayout,
  Box,
  Button,
  Flashbar,
  FlashbarProps,
  Header,
  Input,
  LiveRegion,
  Pagination,
  Select,
  SelectProps,
  Table,
  TableProps
} from '@amzn/awsui-components-core';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAnnouncements } from 'src/api/app-sync-services';
import { AnnouncementFlatTableEntity, Application } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { HomePageSideNavigationV2 } from 'src/components/side-navigation-v2/HomePageSideNavigationV2';
import { Preferences, TableEmptyState, TableNoMatchState, getMatchesCountText } from 'src/components/generic-components/TableCommons';
import { eUserPreferenceKeys } from 'src/constants/AppConstants';
import { useColumnWidths } from 'src/hooks/useColumnWidths';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { appLayoutAriaLabels, paginationAriaLabels } from 'src/i18n-strings';
import { logger } from 'src/logger';
import { dateTimeComparatorForTable } from 'src/utilities/DateTimeUtilities';
import { AnnouncementCategories } from '../admin-components/admin-announcements/AdminManageAnnouncements';
import { Titles, UserActionsEventTypes } from '../das-finsuite-constants';
import './Announcements.scss';
import * as AnnouncementsTableConfig from './AnnouncementsTableConfig';

const SEARCHABLE_COLUMNS: (keyof AnnouncementFlatTableEntity)[] = ['title', 'applicationName', 'announcementCategory', 'createdBy'];

const defaultApplication: SelectProps.Option = { value: '0', label: 'Any Application' };
const defaultCategory: SelectProps.Option = { value: '0', label: 'Any Category' };

function prepareAppOptions(apps: Application[]): SelectProps.Options {
  return [defaultApplication, ...apps.filter((app) => app.applicationName).map((app) => ({ label: app.applicationName!, value: app.id }))];
}

function prepareCategoryOptions(): SelectProps.Options {
  return [defaultCategory, ...AnnouncementCategories.map((cat) => ({ label: cat.label!, value: cat.value! }))];
}

function matchesApplication(item: AnnouncementFlatTableEntity, selected: SelectProps.Option): boolean {
  return selected === defaultApplication || item.applicationName === selected.label;
}

function matchesCategory(item: AnnouncementFlatTableEntity, selected: SelectProps.Option): boolean {
  return selected === defaultCategory || item.announcementCategory === selected.label;
}

const Announcements: React.FC = () => {
  const appLayout = useRef<any>();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { listOfApplications, setAnnouncements, announcementsFlatEntity, setAnnouncementsFlatEntity, isAdminControlsEnabled } = useAppContext();
  const [appsInAnnouncements, setAppsInAnnouncements] = useState<Application[]>([]);
  const [announcementsLoadingStatus, setAnnouncementsLoadingStatus] = useState(false);
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const [colDefs, saveWidths] = useColumnWidths(
    eUserPreferenceKeys.ANNOUNCEMENTS_TABLE_WIDTHS,
    AnnouncementsTableConfig.ANNOUNCEMENT_COLUMN_DEFINITIONS
  );
  const [preferences, setPreferences] = usePersistedState(
    eUserPreferenceKeys.ANNOUNCEMENTS_TABLE_PREFERENCES,
    AnnouncementsTableConfig.ANNOUNCEMENTS_DEFAULT_PREFERENCES
  );

  const [selectedApplication, setSelectedApplication] = useState<SelectProps.Option>(defaultApplication);
  const [selectedCategory, setSelectedCategory] = useState<SelectProps.Option>(defaultCategory);

  useEffect(() => {
    if (state?.fromAppName) {
      setSelectedApplication({ label: state.fromAppName, value: state.fromAppName });
    }
    if (state?.filterByTitle) {
      actions.setFiltering(state.filterByTitle);
    }
  }, [state]);

  useEffect(() => {
    const appIds = announcementsFlatEntity.filter((item) => item.isActive).map((a) => a.applicationId);
    const uniqueApps = listOfApplications
      .filter((app: Application) => app.itemMetadata.isActive && appIds.includes(app.id))
      .filter((app, i, arr) => arr.findIndex((a) => a.id === app.id) === i)
      .sort((a, b) => (a.applicationName && b.applicationName ? a.applicationName.localeCompare(b.applicationName) : -1));
    setAppsInAnnouncements(uniqueApps);
  }, [announcementsFlatEntity]);

  useEffect(() => {
    refreshAnnouncements();
  }, []);

  const refreshAnnouncements = async () => {
    setAnnouncementsLoadingStatus(true);
    const parsedAnnouncements = await fetchAnnouncements(listOfApplications);
    setAnnouncements(parsedAnnouncements.announcementList);
    setAnnouncementsFlatEntity(parsedAnnouncements.announcementsFlatTableEntity);
    setAnnouncementsLoadingStatus(false);
  };

  function clearFilter() {
    actions.setFiltering('');
    setSelectedApplication(defaultApplication);
    setSelectedCategory(defaultCategory);
  }

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    announcementsFlatEntity.filter((item) => item.isActive),
    {
      filtering: {
        empty: <TableEmptyState resourceName="Announcements" />,
        noMatch: <TableNoMatchState onClearFilter={clearFilter} />,
        filteringFunction: (item, filteringText) => {
          if (!matchesApplication(item, selectedApplication)) return false;
          if (!matchesCategory(item, selectedCategory)) return false;
          const text = filteringText.toLowerCase();
          return (
            !text ||
            SEARCHABLE_COLUMNS.some((key) => {
              const val = item[key];
              return typeof val === 'string' && val.toLowerCase().includes(text);
            })
          );
        }
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {
        defaultState: {
          sortingColumn: {
            sortingField: 'announcementDate',
            sortingComparator: (a: AnnouncementFlatTableEntity, b: AnnouncementFlatTableEntity) =>
              dateTimeComparatorForTable(a.announcementDate, b.announcementDate)
          },
          isDescending: true
        }
      }
    }
  );

  useLayoutEffect(() => {
    collectionProps.ref.current?.scrollToTop();
  }, [selectedApplication, selectedCategory, collectionProps.ref, filterProps.filteringText]);

  const hasActiveFilters = filterProps.filteringText || selectedApplication !== defaultApplication || selectedCategory !== defaultCategory;

  return (
    <AppLayout
      ref={appLayout}
      contentType="table"
      ariaLabels={appLayoutAriaLabels}
      toolsHide={true}
      navigation={<HomePageSideNavigationV2 />}
      stickyNotifications
      notifications={<Flashbar items={flashbarItems} />}
      content={
        <Table
          {...collectionProps}
          variant="full-page"
          stickyHeader={true}
          enableKeyboardNavigation={true}
          loading={announcementsLoadingStatus}
          loadingText="Loading Announcements..."
          items={items}
          columnDefinitions={colDefs as TableProps.ColumnDefinition<AnnouncementFlatTableEntity>[]}
          onColumnWidthsChange={saveWidths as any}
          visibleColumns={preferences.visibleContent}
          resizableColumns={true}
          wrapLines={preferences.wrapLines}
          stripedRows={preferences.stripedRows}
          contentDensity={preferences.contentDensity}
          header={
            <Box padding={{ top: 'm' }}>
              <Header
                actions={
                  isAdminControlsEnabled ? (
                    <Button variant="primary" onClick={() => navigate('/admin/announcements/new')} iconName="add-plus">
                      New Announcement
                    </Button>
                  ) : undefined
                }
                variant="awsui-h1-sticky"
              >
                {Titles.TITLES_ANNOUNCEMENTS}
              </Header>
            </Box>
          }
          pagination={<Pagination {...paginationProps} ariaLabels={paginationAriaLabels(paginationProps.pagesCount)} />}
          filter={
            <div className="input-container">
              <div className="input-filter">
                <Input
                  type="search"
                  value={filterProps.filteringText}
                  onChange={(event) => actions.setFiltering(event.detail.value)}
                  placeholder="Search"
                  ariaLabel="Search"
                  clearAriaLabel="clear"
                />
              </div>
              <div className="select-filter">
                <Select
                  inlineLabelText="Filter Application"
                  options={prepareAppOptions(appsInAnnouncements)}
                  selectedOption={selectedApplication}
                  onChange={({ detail }) => {
                    setSelectedApplication(detail.selectedOption);
                    logger.info('DaS FinSuite -> User Action: Announcements -> Application Filter Change', {
                      event_type: UserActionsEventTypes.ANNOUNCEMENTS_FILTER_BY_APP,
                      value: detail.selectedOption.label
                    });
                  }}
                  expandToViewport
                />
              </div>
              <div className="select-filter">
                <Select
                  inlineLabelText="Filter Category"
                  options={prepareCategoryOptions()}
                  selectedOption={selectedCategory}
                  onChange={({ detail }) => {
                    setSelectedCategory(detail.selectedOption);
                    logger.info('DaS FinSuite -> User Action: Announcements -> Category Filter Change', {
                      event_type: UserActionsEventTypes.ANNOUNCEMENTS_FILTER_BY_CATEGORY,
                      value: detail.selectedOption.label
                    });
                  }}
                  expandToViewport
                />
              </div>
              <LiveRegion>{hasActiveFilters && <span className="filtering-results">{getMatchesCountText(filteredItemsCount ?? 0)}</span>}</LiveRegion>
            </div>
          }
          preferences={
            <Preferences
              preferences={preferences}
              setPreferences={setPreferences}
              visibleContentOptions={AnnouncementsTableConfig.DEFAULT_ANNOUNCEMENT_VISIBLE_CONTENT_OPTIONS}
              disablePageSizeOptions={false}
              disableResizableColumnsOptions={true}
            />
          }
        />
      }
    />
  );
};

export default Announcements;
